import { faCalendar, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { usePopover } from '../../hooks/usePopover';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import Popover from '../common/Popover';
import { DATE_RANGE_OPTIONS } from './DateRangePillSelector';

// Single date picker props (backward compatibility)
interface SingleDatePickerProps {
	mode?: 'single';
	selected?: Date | null;
	onChange: (date: Date | null) => void;
	placeholderText?: string;
	minDate?: Date;
	maxDate?: Date;
	disabled?: boolean;
	error?: boolean;
	dateFormat?: string;
	className?: string;
	fullWidth?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
}

// Date range picker props
interface DateRangePickerProps {
	mode: 'range';
	startDate?: Date | null;
	endDate?: Date | null;
	onChange: (dates: [Date | null, Date | null]) => void;
	placeholderText?: string;
	minDate?: Date;
	maxDate?: Date;
	disabled?: boolean;
	error?: boolean;
	dateFormat?: string;
	className?: string;
	fullWidth?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	allowClear?: boolean;
}

type DatePickerProps = SingleDatePickerProps | DateRangePickerProps;

export function DatePicker(props: DatePickerProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

	// Extract common props
	const {
		placeholderText = props.mode === 'range'
			? 'Select date range...'
			: 'Select date...',
		minDate,
		maxDate,
		disabled,
		error,
		dateFormat = 'MM/dd/yyyy',
		className,
		fullWidth = true,
		size = 'md',
		allowClear = false,
	} = props;

	const {
		isOpen,
		open,
		close,
		triggerRef,
		popoverRef,
		getPopoverStyle,
		getCaretProps,
	} = usePopover({
		spacing: 8,
		placement: 'auto',
	});

	// Size-based styling to match MultiSelect exactly
	const sizeClasses = {
		xs: 'px-1.5 py-2 text-xs',
		sm: 'px-2.5 py-1.5 text-sm',
		md: 'px-3 py-2 text-sm',
		lg: 'px-4 py-2.5 text-base',
	}[size];

	// Use exact same minHeight values as MultiSelect
	const minHeightStyle = {
		xs: '22px',
		sm: '30px',
		md: '36px',
		lg: '42px',
	}[size];

	// Add CSS for proper month display (when showing 2 months for range)
	useEffect(() => {
		if (props.mode !== 'range') {
			return;
		}

		const style = document.createElement('style');
		style.textContent = `
      .date-picker-popover .react-datepicker {
        width: auto !important;
        border: none !important;
        background: transparent !important;
      }
      .date-picker-popover .react-datepicker__month-container {
        display: inline-block !important;
        margin: 0 4px !important;
        vertical-align: top !important;
        width: auto !important;
      }
      .date-picker-popover .react-datepicker__month {
        margin: 0 !important;
      }
      /* Hide redundant month/year text label on left calendar (has dropdowns) */
      .date-picker-popover .react-datepicker__month-container:first-of-type .react-datepicker__current-month {
        display: none !important;
      }
      /* Center the header content (dropdowns + navigation) */
      .date-picker-popover .react-datepicker__header {
        background: transparent !important;
        border-bottom: none !important;
        padding-top: 0 !important;
      }
    `;
		document.head.appendChild(style);

		return () => {
			if (document.head.contains(style)) {
				document.head.removeChild(style);
			}
		};
	}, [props.mode]);

	// Format date for display (short format to fit narrow containers)
	const formatDate = (date: Date | null) => {
		if (date == null || isNaN(date.getTime())) {
			return '';
		}

		return date.toLocaleDateString('en-US', {
			year: '2-digit',
			month: 'numeric',
			day: 'numeric',
		});
	};

	// Format date range for display with semantic labels for partial ranges
	const formatDateRange = (start: Date | null, end: Date | null): string => {
		if (!start && !end) {
			return '';
		}

		if (start && end) {
			return `${formatDate(start)} - ${formatDate(end)}`;
		}

		if (start) {
			return `${formatDate(start)} ${t('onwards')}`;
		}

		return end ? `${t('Through')} ${formatDate(end)}` : '';
	};

	// Get display value based on mode
	const getDisplayValue = () => {
		if (props.mode === 'range') {
			return formatDateRange(props.startDate || null, props.endDate || null);
		} else {
			return formatDate(props.selected || null);
		}
	};

	const handleInputClick = () => {
		if (disabled) {
			return;
		}
		open();
	};

	// Handle date selection based on mode
	const handleDateSelect = (
		dateOrDates: Date | null | [Date | null, Date | null],
	) => {
		if (props.mode === 'range') {
			// Range mode - expect array of dates
			const dates = dateOrDates as [Date | null, Date | null];
			const [start, end] = dates;

			props.onChange([start, end]);

			// Close only when both dates are selected
			if (start && end) {
				close();
			}
		} else {
			// Single mode - expect single date
			const date = dateOrDates as Date | null;
			props.onChange(date);
			close();
		}
	};

	// Handle clear button click
	const handleClear = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (props.mode === 'range') {
			props.onChange([null, null]);
		} else {
			props.onChange(null);
		}
	};

	// Determine if clear button should be shown
	const hasValue =
		props.mode === 'range' ? props.startDate || props.endDate : props.selected;
	const showClearButton = allowClear && hasValue && !disabled;

	// Handle preset pill click (range mode only)
	const handlePresetClick = (presetId: string) => {
		if (props.mode !== 'range') {
			return;
		}

		if (presetId === 'custom') {
			setSelectedPreset('custom');

			return;
		}

		const option = DATE_RANGE_OPTIONS.find((o) => o.id === presetId);

		if (option?.value.startDate != null && option?.value.endDate != null) {
			setSelectedPreset(presetId);
			props.onChange([option.value.startDate, option.value.endDate]);
			close();
		}
	};

	// Handle individual date clear (range mode only)
	const handleClearStart = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (props.mode === 'range') {
			setSelectedPreset('custom');
			props.onChange([null, props.endDate ?? null]);
		}
	};

	const handleClearEnd = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (props.mode === 'range') {
			setSelectedPreset('custom');
			props.onChange([props.startDate ?? null, null]);
		}
	};

	// Format date for display in From/To fields
	const formatDateForField = (date: Date | null): string => {
		if (date == null || isNaN(date.getTime())) {
			return '';
		}

		return date.toLocaleDateString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	};

	return (
		<>
			<div className={cn('relative', fullWidth && 'w-full')}>
				<input
					ref={triggerRef as React.RefObject<HTMLInputElement>}
					type="text"
					value={getDisplayValue()}
					placeholder={placeholderText}
					onClick={handleInputClick}
					readOnly
					disabled={disabled}
					style={{ minHeight: minHeightStyle }}
					className={cn(
						// Base styling to match other form controls
						'block cursor-pointer text-left truncate',
						fullWidth && 'w-full',
						showClearButton ? 'pr-20' : 'pr-8',
						sizeClasses,
						'border rounded-md shadow-sm transition-colors duration-200',
						colors.classes.textPrimary,
						colors.classes.bgPrimary,
						'placeholder-gray-400 dark:placeholder-gray-400',

						error
							? [
									colors.classes.borderError,
									'focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:focus:border-red-400',
								]
							: [
									colors.classes.borderDefault,
									'focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 focus:outline-none',
								],
						disabled &&
							cn(
								colors.classes.bgSecondary,
								'cursor-not-allowed opacity-75',
								colors.classes.borderDisabled,
							),
						className,
					)}
				/>

				{/* Clear button — matches BaseTextInput's clear: just a color
				    transition on the icon, no background, cursor-pointer. */}
				{showClearButton && (
					<>
						<div
							role="button"
							tabIndex={0}
							onClick={handleClear}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									handleClear(e as any);
								}
							}}
							className={cn(
								'absolute inset-y-0 right-10 flex items-center justify-center w-6 cursor-pointer',
							)}
						>
							<FontAwesomeIcon
								icon={faXmark}
								className={cn(
									colors.classes.textSecondary,
									'hover:' + colors.classes.textPrimary,
									'transition-colors',
								)}
								style={{ fontSize: '14px' }}
								aria-hidden="true"
							/>
						</div>
						{/* Separator */}
						<div
							className={cn(
								'absolute inset-y-0 right-8 w-px bg-gray-300 dark:bg-gray-600',
							)}
							style={{
								top: '6px',
								bottom: '6px',
							}}
						/>
					</>
				)}

				{/* Calendar icon */}
				<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center justify-center w-8">
					<FontAwesomeIcon
						icon={faCalendar}
						className={cn('h-3 w-3', colors.classes.textSecondary)}
						style={{ fontSize: '14px' }}
						aria-hidden="true"
					/>
				</span>
			</div>

			<Popover
				isOpen={isOpen}
				style={getPopoverStyle()}
				caretProps={getCaretProps(8)}
				className="date-picker-popover"
				contentRef={popoverRef}
			>
				{props.mode === 'range' ? (
					<div className={cn('p-3', colors.classes.bgPrimary, 'rounded-lg')}>
						{/* Preset Pills - constrained to calendar width */}
						<div
							className="flex flex-wrap gap-1.5 mb-3"
							style={{ maxWidth: '496px' }}
						>
							{DATE_RANGE_OPTIONS.map((option) => (
								<button
									key={option.id}
									type="button"
									onClick={() => {
										handlePresetClick(option.id);
									}}
									className={cn(
										'px-2.5 py-1 rounded-full text-xs font-medium transition-colors',
										selectedPreset === option.id
											? cn(colors.classes.filterPillActive, 'hover:opacity-90')
											: cn(
													colors.classes.bgSecondary,
													colors.classes.textPrimary,
													'hover:opacity-80',
												),
									)}
								>
									{t(option.label)}
								</button>
							))}
						</div>

						{/* Date Selection Container - wraps From/To fields and calendars together */}
						<div
							className={cn(
								'border rounded-lg p-3',
								colors.classes.borderDefault,
							)}
						>
							{/* From/To Date Fields - aligned with calendar columns */}
							<div className="flex mb-3">
								{/* From Date - matches left calendar width */}
								<div className="flex-1 pr-1">
									<label
										className={cn(
											'block text-xs font-medium mb-1',
											colors.classes.textSecondary,
										)}
									>
										{t('From')}
									</label>
									<div className="relative">
										<input
											type="text"
											readOnly
											value={formatDateForField(props.startDate ?? null)}
											placeholder={t('Start date')}
											className={cn(
												'w-full px-2 py-1.5 text-xs border rounded',
												colors.classes.bgPrimary,
												colors.classes.textPrimary,
												colors.classes.borderDefault,
												'placeholder-gray-400',
											)}
										/>
										{props.startDate != null && (
											<button
												type="button"
												onClick={handleClearStart}
												className={cn(
													'absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600',
													colors.classes.textSecondary,
												)}
											>
												<FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
											</button>
										)}
									</div>
								</div>

								{/* To Date - matches right calendar width */}
								<div className="flex-1 pl-1">
									<label
										className={cn(
											'block text-xs font-medium mb-1',
											colors.classes.textSecondary,
										)}
									>
										{t('To')}
									</label>
									<div className="relative">
										<input
											type="text"
											readOnly
											value={formatDateForField(props.endDate ?? null)}
											placeholder={t('End date')}
											className={cn(
												'w-full px-2 py-1.5 text-xs border rounded',
												colors.classes.bgPrimary,
												colors.classes.textPrimary,
												colors.classes.borderDefault,
												'placeholder-gray-400',
											)}
										/>
										{props.endDate != null && (
											<button
												type="button"
												onClick={handleClearEnd}
												className={cn(
													'absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600',
													colors.classes.textSecondary,
												)}
											>
												<FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
											</button>
										)}
									</div>
								</div>
							</div>

							{/* Calendar - dynamically switch mode based on which date is missing */}
							{props.startDate == null && props.endDate != null ? (
								// Selecting start date only (end exists)
								<ReactDatePicker
									selectsStart
									startDate={props.startDate}
									endDate={props.endDate}
									monthsShown={2}
									shouldCloseOnSelect={false}
									onChange={(date) => {
										setSelectedPreset('custom');

										if (date != null && props.endDate != null) {
											// Auto-swap if selected start > existing end
											if (date > props.endDate) {
												props.onChange([props.endDate, date]);
											} else {
												props.onChange([date, props.endDate]);
											}
										}
									}}
									minDate={minDate}
									maxDate={maxDate}
									dateFormat={dateFormat}
									showYearDropdown={true}
									showMonthDropdown={true}
									dropdownMode="select"
									inline
									fixedHeight
									showMonthYearPicker={false}
									isClearable={false}
								/>
							) : props.startDate != null && props.endDate == null ? (
								// Selecting end date only (start exists)
								<ReactDatePicker
									selectsEnd
									startDate={props.startDate}
									endDate={props.endDate}
									monthsShown={2}
									shouldCloseOnSelect={false}
									onChange={(date) => {
										setSelectedPreset('custom');

										if (date != null && props.startDate != null) {
											// Auto-swap if selected end < existing start
											if (date < props.startDate) {
												props.onChange([date, props.startDate]);
											} else {
												props.onChange([props.startDate, date]);
											}
										}
									}}
									minDate={minDate}
									maxDate={maxDate}
									dateFormat={dateFormat}
									showYearDropdown={true}
									showMonthDropdown={true}
									dropdownMode="select"
									inline
									fixedHeight
									showMonthYearPicker={false}
									isClearable={false}
								/>
							) : (
								// Normal range selection (both set or both null)
								<ReactDatePicker
									selectsRange
									startDate={props.startDate}
									endDate={props.endDate}
									monthsShown={2}
									shouldCloseOnSelect={false}
									onChange={(dates) => {
										setSelectedPreset('custom');
										handleDateSelect(dates);
									}}
									minDate={minDate}
									maxDate={maxDate}
									dateFormat={dateFormat}
									showYearDropdown={true}
									showMonthDropdown={true}
									dropdownMode="select"
									inline
									fixedHeight
									showMonthYearPicker={false}
									isClearable={false}
								/>
							)}
						</div>
					</div>
				) : (
					<ReactDatePicker
						selected={
							props.selected != null && !isNaN(props.selected.getTime())
								? props.selected
								: null
						}
						monthsShown={1}
						shouldCloseOnSelect={true}
						onChange={handleDateSelect}
						minDate={minDate}
						maxDate={maxDate}
						dateFormat={dateFormat}
						showYearDropdown={true}
						showMonthDropdown={true}
						dropdownMode="select"
						inline
						fixedHeight
						showMonthYearPicker={false}
						isClearable={false}
						className="shadow-lg border border-gray-200 rounded-lg relative z-10 bg-white"
					/>
				)}
			</Popover>
		</>
	);
}

// Export type for external use
export type { DatePickerProps };
